<template>
  <div class="flex flex-col min-h-screen">
    <header class="flex flex-col w-full border-b">
      <Navbar :activeTab="1" />
    </header>
    <div v-if="!userStore.selectedProgram" class="flex flex-col">
      <ProgramsList @programSelected="handleProgramSelected" />
    </div>
    <div v-else class="flex flex-col">
      <main class="flex flex-col w-full pt-12 pl-6 gap-4">
        <div class="flex flex-col w-full">
          <span class="text-black text-3xl font-semibold leading-9 text-left">{{
            userStore.selectedProgram?.name
          }}</span>
        </div>
        <div class="flex flex-col w-full">
          <div class="flex flex-wrap gap-2 items-start gap-6">
            <div
              class="flex flex-col"
              :class="{
                'border-b border-primary-purple text-primary-purple':
                  activeTab === 0,
                'text-gray-500': activeTab !== 0,
              }"
              @click="setActiveTab(0)"
            >
              <span class="text-sm font-medium leading-5 cursor-pointer"
                >Expert Program Details</span
              >
            </div>
            <div
              class="flex flex-col"
              :class="{
                'border-b border-primary-purple text-primary-purple':
                  activeTab === 1,
                'text-gray-500': activeTab !== 1,
              }"
              @click="setActiveTab(1)"
            >
              <span class="text-sm font-medium leading-5 cursor-pointer"
                >Booking System</span
              >
            </div>
            <div
              class="flex flex-col"
              :class="{
                'border-b border-primary-purple text-primary-purple':
                  activeTab === 2,
                'text-gray-500': activeTab !== 2,
              }"
              @click="setActiveTab(2)"
            >
              <span class="text-sm font-medium leading-5 cursor-pointer"
                >Widgets</span
              >
            </div>
            <div
              class="flex flex-col"
              :class="{
                'border-b border-primary-purple text-primary-purple':
                  activeTab === 3,
                'text-gray-500': activeTab !== 3,
              }"
              @click="setActiveTab(3)"
            >
              <span class="text-sm font-medium leading-5 cursor-pointer"
                >Experts</span
              >
            </div>
            <div
              class="flex flex-col"
              :class="{
                'border-b border-primary-purple text-primary-purple':
                  activeTab === 4,
                'text-gray-500': activeTab !== 4,
              }"
              @click="setActiveTab(4)"
            >
              <span class="text-sm font-medium leading-5 cursor-pointer"
                >Advanced Program Settings</span
              >
            </div>
          </div>
        </div>

        <div v-if="activeTab === 0">
          <ProgramDetailsContainer />
        </div>
        <div v-if="activeTab === 1">
          <DirectoryContainer />
        </div>
        <div v-if="activeTab === 2">
          <ViewWidget />
        </div>
        <div v-if="activeTab === 3">
          <ExpertsContainer />
        </div>
        <div v-if="activeTab === 4">
          <AdvancedSettingsContainer
            :showAdvancedSettings="showAdvancedSettings"
          />
        </div>
      </main>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, computed } from "vue";
import Navbar from "@/components/dashboard/Navbar.vue";
import DirectoryContainer from "@/components/dashboard/programs/directory-and-widget/DirectoryPageContainer.vue";
import ViewWidget from "@/components/dashboard/programs/directory-and-widget/ViewWidget.vue";
import ProgramDetailsContainer from "@/components/dashboard/programs/program-details/PageContainer.vue";
import ExpertsContainer from "@/components/dashboard/programs/experts/PageContainer.vue";
import AdvancedSettingsContainer from "@/components/dashboard/programs/program-details/AdvancedSettingsContainer.vue";
import { useUserStore } from "@/stores/user";
import ProgramsList from "@/components/dashboard/programs/ProgramsList.vue";
import { Program } from "@/types/dashboard-types";
import { useRoute, useRouter } from "vue-router";
import { useQuery } from "@vue/apollo-composable";
import { gql } from "@apollo/client/core";
import type { SubscriptionPlan } from "@/types/subscription-types";

const LIST_SUBSCRIPTION_PLANS = gql`
  query ListSubscriptionPlans($currentSubscription: Boolean) {
    listSubscriptionPlans(currentSubscription: $currentSubscription) {
      currentSubscription {
        subscriptionPlanTierId
      }
      allPlans {
        name
        subscriptionPlanTiers {
          id
        }
      }
    }
  }
`;

export default defineComponent({
  name: "BillingPage",
  components: {
    Navbar,
    DirectoryContainer,
    ProgramDetailsContainer,
    ProgramsList,
    ExpertsContainer,
    AdvancedSettingsContainer,
    ViewWidget,
  },
  setup() {
    const activeTab = ref(0);
    const userStore = useUserStore();
    // const programSelected = ref(false);
    const route = useRoute();
    const router = useRouter();

    const currentActiveSubscriptionPlanTierId = ref("");
    const allPlans = ref<SubscriptionPlan[]>([]);
    const { onResult } = useQuery(LIST_SUBSCRIPTION_PLANS, {
      currentSubscription: true,
    });
    onResult((result) => {
      if (result?.data?.listSubscriptionPlans) {
        currentActiveSubscriptionPlanTierId.value =
          result.data.listSubscriptionPlans.currentSubscription
            ?.subscriptionPlanTierId || "";
        allPlans.value = result.data.listSubscriptionPlans.allPlans || [];
      }
    });
    const currentPlanName = computed(() => {
      for (const plan of allPlans.value as SubscriptionPlan[]) {
        if (
          plan.subscriptionPlanTiers &&
          plan.subscriptionPlanTiers.some(
            (tier: { id: string }) =>
              tier.id === currentActiveSubscriptionPlanTierId.value
          )
        ) {
          return plan.name;
        }
      }
      return "";
    });
    const showAdvancedSettings = computed(
      (): boolean =>
        Boolean(currentPlanName.value) &&
        (currentPlanName.value.includes("Growth Plan") ||
          currentPlanName.value.includes("Enterprise Plan"))
    );

    onMounted(() => {
      if (route.query.salesforce_sync && userStore.selectedProgram) {
        setActiveTab(0);
      } else if (route.name === "SuggestedExperts") {
        setActiveTab(3);
      } else if (route.name === "Experts") {
        setActiveTab(3);
      } else {
        if (route.query.tab) {
          setActiveTab(parseInt(route.query.tab as string));
        }
      }
    });

    const setActiveTab = (tab: number) => {
      const getQueryParams = (includeTab = false) => ({
        ...(includeTab && { tab }),
        ...(route.query.salesforce_sync &&
          route.query.salesforce_sync === "true" && {
            salesforce_sync: route.query.salesforce_sync,
          }),
      });

      if (route.name === "SuggestedExperts" || route.name === "Experts") {
        if (tab === 3) {
          router.push({
            name: route.name,
            query: getQueryParams(),
          });
        } else {
          router.push({
            name: "ExpertPrograms",
            query: getQueryParams(true),
          });
        }
      } else {
        router.push({
          path: route.path,
          query: getQueryParams(true),
        });
      }
      activeTab.value = tab;
    };

    const handleProgramSelected = (program: Program) => {
      if (program) {
        userStore.setSelectedProgram(program);
        setActiveTab(0);
      }
    };

    return {
      activeTab,
      handleProgramSelected,
      userStore,
      setActiveTab,
      showAdvancedSettings,
      currentPlanName,
    };
  },
});
</script>
